import { yupResolver } from "@hookform/resolvers/yup";
import { InputAdornment, SelectChangeEvent } from "@mui/material";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useCountryListByGeoScope,
  useFileDownloadMutation,
  useGetCountryListForDropdowns,
  useUpdateContractDocumentMutation,
  useUploadDocumentDetailsMutation,
  useUploadFileMutation,
  useValidateOpportunityIdMutation,
} from "../../../../api/services/client/clientApis";
import {
  DocumentDetailsContract,
  DocumentDetailsOthers,
  DocumentUploadParams,
  ValidateOpportunityPayload,
} from "../../../../api/services/client/models";
import { useGetPaymentTerms } from "../../../../api/services/program/programApis";
import { useGetLineOfBusiness } from "../../../../api/services/programTemplate/programTemplateApis";
import { ReactComponent as CalendarIcon } from "../../../../assets/images/calendar-icon.svg";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiLink,
  MuiModal,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
  WarningConfirmationModal,
} from "../../../../components";
import FileUploader from "../../../../components/fileUpload/FileUpload";
import {
  STATUS_BADGE_TYPE,
  StatusBadgeType,
} from "../../../../components/statusBadge/StatusBadge";
import {
  BE_ERROR_CODES,
  CLIENT_DOCUMENT_TYPES,
  DOCUMENT_SITE_CORE_KEY,
  EMPTY_VALUE_DASH,
  LANGUAGE_CODE,
  MAX_LENGTH_50,
  PAGE_IDS,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import { RefDropdownHandle } from "../../../../helpers/types/common";
import {
  convertToUserFormat,
  convertToUTCFormat,
  getCountryIdsAsStringArray,
  getDocumentStatusFromEffectiveDate,
  getDocumentStatusFromExpirationDate,
  getParsedBEErrors,
  getParsedErrorMessage,
  getValueOrBlank,
  isEmpty,
  isErrorCode,
  isNotEmpty,
  replaceHashWithValue,
  setApiDataToDropDownOption,
  setApiDataToDropDownOptionForCountry,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import {
  Country,
  DocumentType,
  STATUS_YES,
  UploadDocumentFormKey,
} from "../../modelsAndSchemas/Models";
import { UploadDocumentModalValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import {
  documentSiteCoreKeys,
  uploadDocumentSiteCoreKeys,
} from "../../siteCore/SiteCoreKeys";
import { DOCUMENT_TABS_INDEX } from "../createClientDocument/CreateClientDocuments";
import { UploadDocumentModalWrapper } from "./UploadDocument.style";

interface UploadDocumentProps {
  data?: DocumentDetailsOthers | DocumentDetailsContract;
  open: boolean;
  isInEditMode?: boolean;
  onClose: () => void;
  onDocumentUploaded: (documentType?: DocumentType) => void;
  selectedTab?: number;
}

export const UploadDocumentModal: FC<UploadDocumentProps> = ({
  data,
  open,
  isInEditMode = false,
  onClose,
  onDocumentUploaded,
  selectedTab,
}) => {
  const pageId = PAGE_IDS.UPLOAD_DOCUMENT;
  const { gridSizeMedium: gridSize } = useGridContainer(true);

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const { id: clientId } = useSelector(createClientSelector);
  const productLinesRef = useRef<RefDropdownHandle>(null);
  const [docUploadDate, setDocUploadDate] = useState<string>("");
  const dispatch = useDispatch();
  const [isCancelWarningVisible, setIsCancelWarningVisible] = useState(false);
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
  const [disableUpload, setDisableUpload] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [clearFile, setClearFile] = useState<boolean>(false);
  const [geoScopeCountries, setGeoScopeCountries] = useState<string[]>([]);
  const [resetGeoScopeCountries, setResetGeoScopeCountries] =
    useState<boolean>(false);
  const { data: countryListData } = useGetCountryListForDropdowns();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const isNewFileUploadedRef = useRef(false);
  const { isMobile } = useMediaQueries();
  const gridSpacing = isMobile ? 2 : 4;

  const documentTypeRef = useRef<DocumentType>(
    CLIENT_DOCUMENT_TYPES.clientContract as DocumentType,
  );

  const {
    mutateAsync: getCountriesByGeoScope,
    data: geoScopeCountryList,
    isPending: isGeoScopeCountryListPending,
    isSuccess: isGeoScopeCountryListSuccess,
  } = useCountryListByGeoScope();

  const {
    mutateAsync: uploadDocumentDetails,
    isPending: isUploadDocumentDetailsPending,
    isSuccess: isUploadDocumentDetailsSuccess,
    isError: isUploadDocumentDetailsError,
    error: uploadDocumentDetailsError,
  } = useUploadDocumentDetailsMutation();

  const {
    mutateAsync: updateContractDocument,
    isPending: isUpdateContractDocumentPending,
    isSuccess: isUpdateContractDocumentSuccess,
    isError: isUpdateContractDocumentError,
    error: updateContractDocumentError,
  } = useUpdateContractDocumentMutation();

  const {
    mutateAsync: fileUpload,
    isPending: isfileUploadPending,
    isError: isfileUploadError,
    error: fileUploadApiError,
  } = useUploadFileMutation();

  const {
    data: lineOfBusinesses,
    isFetching: businessPending,
    isError: isLobError,
    error: lobError,
  } = useGetLineOfBusiness(LANGUAGE_CODE.EN_US);

  const {
    data: validateOpportunityIdData,
    mutateAsync: validateOpportunityId,
    isError: isValidateOpportunityIdError,
    error: validateOpportunityIdError,
    isSuccess: isValidateOpportunityIdSuccess,
    isPending: isValidateOpportunityIdPending,
  } = useValidateOpportunityIdMutation();

  const {
    mutateAsync: getGetPaymentTerms,
    data: paymentTermsList,
    isPending: isPaymentTermsListPending,
  } = useGetPaymentTerms();

  const [isUploading, setIsUploading] = useState(false);

  const {
    fetchLabel,
    commonValidationMessages,
    commonLabels,
    fetchComponentTitle,
    fetchValidationMessage,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
    fetchMediaListImage,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const {
    uploadDocumentTitle,
    maxFileSizeErrorMessage,
    fileFormatErrorMessage,
    maximumFileSizeAllowed,
    allowedFileTypes,
  } = uploadDocumentSiteCoreKeys;

  const buttonCancel = commonLabels.cancel;
  const buttonUpload = isInEditMode ? commonLabels.update : commonLabels.upload;
  const contractGeoScopeIdRef = useRef<HTMLSelectElement>(null);

  const [minCountriesRequired, setMinCountriesRequired] = useState<number>(2);
  const errorMessageRef = useRef<string>();
  const uploadImage = fetchMediaListImage(
    commonSiteCoreKeys.commonComponentKey,
    commonSiteCoreKeys.browseImage,
    commonSiteCoreKeys.smallImage,
  );
  const methods = useForm({
    mode: "all",
    defaultValues: {
      [UploadDocumentFormKey.documentType]:
        selectedTab === DOCUMENT_TABS_INDEX.OTHERS
          ? DOCUMENT_SITE_CORE_KEY.documentTypeOthers
          : DOCUMENT_SITE_CORE_KEY.documentTypeContract,
      [UploadDocumentFormKey.contractId]: "",
      [UploadDocumentFormKey.opportunityId]: "",
      [UploadDocumentFormKey.opportunityName]: "",
      [UploadDocumentFormKey.isOpportunityIdValidated]: false,
      [UploadDocumentFormKey.ownershipCountryId]: "",
      [UploadDocumentFormKey.exclusivityId]: "",
      [UploadDocumentFormKey.sowAutoRenewalClauseId]: "",
      [UploadDocumentFormKey.sowStatusId]: STATUS_BADGE_TYPE.active,
      [UploadDocumentFormKey.classificationId]: "",
      [UploadDocumentFormKey.serviceLevelAgreementsId]: "",
      [UploadDocumentFormKey.customDeliverablesId]: "",
      [UploadDocumentFormKey.paymentTermId]: "",
      [UploadDocumentFormKey.contractDuration]: "",
      [UploadDocumentFormKey.engagementTypeId]: "",
      [UploadDocumentFormKey.contractGeoScopeId]: "",
      [UploadDocumentFormKey.contractGeoScopeCountries]: [],
      [UploadDocumentFormKey.contractGeoScopeSingleCountry]: "",
      [UploadDocumentFormKey.uploadDate]: "",
      [UploadDocumentFormKey.sowContractEffectiveDate]: "",
      [UploadDocumentFormKey.effectiveDate]: "",
      [UploadDocumentFormKey.notes]: "",
      [UploadDocumentFormKey.statusId]: STATUS_BADGE_TYPE.active,
      [UploadDocumentFormKey.contractProductLines]: [],
    },
    resolver: yupResolver(
      UploadDocumentModalValidationSchema(
        commonValidationMessages,
        getValueOrBlank(userCountryDateFormat),
        fetchValidationMessage,
        fetchLabel,
        minCountriesRequired,
        errorMessageRef.current,
      ),
    ),
  });

  const {
    control,
    getValues,
    watch,
    handleSubmit,
    setValue,
    reset,
    formState,
    setError,
    clearErrors,
    trigger,
  } = methods;

  const handleOtherDocumentEffectiveDateChange = (
    newValue: dayjs.Dayjs | null,
  ) => {
    if (isNotEmpty(newValue)) {
      const today = dayjs();
      const existingStatus = getValues(UploadDocumentFormKey.statusId);
      if (newValue.isAfter(today, "day")) {
        setValue(
          UploadDocumentFormKey.statusId,
          STATUS_BADGE_TYPE.awaitingEffectiveDate,
        );
        // when last status is awaitingEffectiveDate and effective date is changed to past or today
      } else if (existingStatus === STATUS_BADGE_TYPE.awaitingEffectiveDate) {
        setValue(UploadDocumentFormKey.statusId, STATUS_BADGE_TYPE.active);
      }
    }
  };

  const { dirtyFields, isValid } = formState;

  useEffect(() => {
    if (!isInEditMode) setDisableUpload(false);
    else setDisableUpload(!isValid || !!fileUploadError);
  }, [isValid, fileUploadError]);

  useEffect(() => {
    if (geoScopeCountries && isGeoScopeCountryListSuccess) {
      const countries = geoScopeCountries.map((country) => country);
      setGeoScopeCountries(countries);
    }
  }, [geoScopeCountryList]);

  const {
    mutate: downloadFile,
    isPending: isPendingOnDownload,
    isSuccess: isSuccessOnDownload,
    data: dataOnDownload,
  } = useFileDownloadMutation();

  const [tooltipMessage, setTooltipMessage] = useState<string>("");
  const [showTooltipErrorIcon, setShowTooltipErrorIcon] =
    useState<boolean>(false);

  const getDropdownList = (key: keyof typeof uploadDocumentSiteCoreKeys) => {
    return fetchDropdownValues(
      uploadDocumentTitle,
      uploadDocumentSiteCoreKeys[key],
    );
  };

  useEffect(() => {
    if (dataOnDownload) {
      const file = new File(
        [dataOnDownload.blob],
        getValueOrBlank(data?.documentName),
        {
          type: dataOnDownload.blob.type,
        },
      );
      setUploadedFile(file);
      setValue(UploadDocumentFormKey.file, file);
    }
  }, [isSuccessOnDownload, dataOnDownload]);

  const watchStatusId = useWatch({
    control,
    name: UploadDocumentFormKey.statusId,
  });

  const allStatusOptions = getDropdownList("othersStatusList");

  const filteredStatusOptions = useMemo(() => {
    if (watchStatusId === STATUS_BADGE_TYPE.awaitingEffectiveDate) {
      return allStatusOptions.filter(
        (option) => option.value === STATUS_BADGE_TYPE.awaitingEffectiveDate,
      );
    }
    return allStatusOptions.filter(
      (option) => option.value !== STATUS_BADGE_TYPE.awaitingEffectiveDate,
    );
  }, [allStatusOptions, watchStatusId]);

  const isProductLineMount = useMemo(() => {
    return productLinesRef?.current !== null;
  }, [productLinesRef?.current]);

  useEffect(() => {
    if (data && isNotEmpty(data) && isInEditMode) {
      setDocUploadDate(formatDate(data.uploadDate));

      const isContractDocument =
        data.documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract;
      const isOthersDocument =
        data.documentType === DOCUMENT_SITE_CORE_KEY.documentTypeOthers;

      if (isContractDocument && isProductLineMount) {
        handleContractData(data as DocumentDetailsContract);
      }

      if (isOthersDocument) {
        handleOtherData(data as DocumentDetailsOthers);
      }

      downloadFile({
        documentId: getValueOrBlank(data?.documentId),
        documentType: isContractDocument
          ? CLIENT_DOCUMENT_TYPES.clientContract
          : CLIENT_DOCUMENT_TYPES.clientOthers,
      });
    }
  }, [isInEditMode, data, isProductLineMount]);

  const handleContractData = (contractData: DocumentDetailsContract) => {
    const {
      uploadDate,
      classificationId,
      contractGeoScopeCountries,
      contractGeoScopeId,
      exclusivityId,
      notes,
      status,
      contractDuration,
      customDeliverablesId,
      engagementTypeId,
      paymentTerms,
      serviceLevelAgreementsId,
      sowAutoRenewalClauseId,
      sowAutoRenewalTerms,
      sowExpirationDate,
      sowContractEffectiveDate,
      ownershipCountry,
      productLines,
      opportunityId,
      opportunityName,
      contractId,
    } = contractData;

    const productLineIds = productLines?.map((pl) => pl.id) || [];
    const selectedGeoScopeCountries =
      getCountryIdsAsStringArray(
        (contractData.contractGeoScopeCountries ?? []) as Country[],
      ) ?? [];
    setGeoScopeCountries(selectedGeoScopeCountries);
    getCountriesByGeoScope({ geoScopeId: contractGeoScopeId });
    reset({
      [UploadDocumentFormKey.documentType]:
        DOCUMENT_SITE_CORE_KEY.documentTypeContract,
      [UploadDocumentFormKey.classificationId]:
        getValueOrBlank(classificationId),
      [UploadDocumentFormKey.contractGeoScopeCountries]:
        selectedGeoScopeCountries,
      [UploadDocumentFormKey.contractGeoScopeId]:
        getValueOrBlank(contractGeoScopeId),
      [UploadDocumentFormKey.sowStatusId]: status,
      [UploadDocumentFormKey.contractDuration]: contractDuration
        ? String(contractDuration)
        : "",
      [UploadDocumentFormKey.customDeliverablesId]:
        getValueOrBlank(customDeliverablesId),
      [UploadDocumentFormKey.engagementTypeId]:
        getValueOrBlank(engagementTypeId),
      [UploadDocumentFormKey.exclusivityId]: getValueOrBlank(exclusivityId),
      [UploadDocumentFormKey.notes]: getValueOrBlank(notes),
      [UploadDocumentFormKey.ownershipCountryId]: getValueOrBlank(
        ownershipCountry?.countryId,
      ),
      [UploadDocumentFormKey.paymentTermId]: getValueOrBlank(paymentTerms?.id),
      [UploadDocumentFormKey.serviceLevelAgreementsId]: getValueOrBlank(
        serviceLevelAgreementsId,
      ),
      [UploadDocumentFormKey.sowAutoRenewalClauseId]: getValueOrBlank(
        sowAutoRenewalClauseId,
      ),
      [UploadDocumentFormKey.sowAutoRenewalTerms]: sowAutoRenewalTerms
        ? String(sowAutoRenewalTerms)
        : "",
      [UploadDocumentFormKey.sowContractEffectiveDate]: formatDate(
        sowContractEffectiveDate,
      ),
      [UploadDocumentFormKey.uploadDate]: formatDate(uploadDate),
      [UploadDocumentFormKey.sowExpirationDate]: formatDate(sowExpirationDate),
      [UploadDocumentFormKey.contractGeoScopeSingleCountry]:
        getSingleGeoScopeCountry(contractGeoScopeCountries),
      [UploadDocumentFormKey.sowEffectiveDateWithoutFormatted]: dayjs(
        contractData.sowContractEffectiveDate,
      ),
      [UploadDocumentFormKey.contractProductLines]: productLineIds,
      [UploadDocumentFormKey.sowExpiryDateWithoutFormatted]:
        contractData?.sowExpirationDate
          ? dayjs(contractData.sowExpirationDate).utc(false).startOf("day") // ignore extra 23:59:59 hour min sec
          : null,
      [UploadDocumentFormKey.opportunityId]: opportunityId,
      [UploadDocumentFormKey.opportunityName]: opportunityName,
      [UploadDocumentFormKey.isOpportunityIdValidated]: true,
      [UploadDocumentFormKey.contractId]: contractId,
    });
    if (isNotEmpty(productLineIds) && productLinesRef.current) {
      productLinesRef.current.resetValues(productLineIds);
    }
  };

  const handleOtherData = (otherData: DocumentDetailsOthers) => {
    const { uploadDate, notes, effectiveDate, statusId } = otherData;

    reset({
      [UploadDocumentFormKey.documentType]:
        DOCUMENT_SITE_CORE_KEY.documentTypeOthers,
      [UploadDocumentFormKey.effectiveDate]: formatDate(effectiveDate),
      [UploadDocumentFormKey.notes]: getValueOrBlank(notes),
      [UploadDocumentFormKey.uploadDate]: formatDate(uploadDate),
    });
    setTimeout(() => {
      setValue(UploadDocumentFormKey.statusId, getValueOrBlank(statusId));
    }, 100);
  };

  const formatDate = (date?: string) =>
    date ? convertToUserFormat(date, userCountryDateFormat) : "";

  const getSingleGeoScopeCountry = (countries: string[] | Country[]) => {
    if (!Array.isArray(countries) || countries.length === 0) {
      return undefined;
    }

    const firstCountry = countries[0];
    if (typeof firstCountry === "string") {
      return firstCountry;
    }
    return firstCountry?.countryId;
  };

  const handleChangeGeoScope = (contractGeoScopeId: string) => {
    if (
      contractGeoScopeId &&
      contractGeoScopeId.toLowerCase() ===
        commonLabels.selectPlaceHolder?.toLowerCase()
    )
      return;
    if (isNotEmpty(contractGeoScopeId)) {
      getCountriesByGeoScope({ geoScopeId: contractGeoScopeId });
      setValue(UploadDocumentFormKey.contractGeoScopeCountries, []);
      setGeoScopeCountries([]);
      setResetGeoScopeCountries(!resetGeoScopeCountries);
      clearErrors(UploadDocumentFormKey.contractGeoScopeCountries);
    }
  };

  const handleDocumentTypeChange = (documentType: string) => {
    if (documentType) {
      reset((formValues) => ({
        ...formValues,
        [UploadDocumentFormKey.contractGeoScopeId]: "",
        [UploadDocumentFormKey.contractGeoScopeCountries]: [],
        [UploadDocumentFormKey.ownershipCountryId]: "",
        [UploadDocumentFormKey.exclusivityId]: "",
        [UploadDocumentFormKey.notes]: "",
        [UploadDocumentFormKey.classificationId]: "",
      }));
      setClearFile(true);
      setTimeout(() => setClearFile(false), 200);
    }
  };

  const contractAutoRenewalClauseWatch = watch(
    UploadDocumentFormKey.sowAutoRenewalClauseId,
  );

  const isContractAutoRenewalClause = useMemo(() => {
    if (isEmpty(contractAutoRenewalClauseWatch)) {
      return undefined;
    } else {
      return contractAutoRenewalClauseWatch === STATUS_YES;
    }
  }, [contractAutoRenewalClauseWatch]);

  useEffect(() => {
    if (isfileUploadPending) {
      setIsUploading(isfileUploadPending);
    }
  }, [isfileUploadPending]);
  const isPending =
    isUploading ||
    isGeoScopeCountryListPending ||
    isPendingOnDownload ||
    isUploadDocumentDetailsPending ||
    isUpdateContractDocumentPending ||
    businessPending ||
    isPaymentTermsListPending ||
    isValidateOpportunityIdPending;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
      }),
    );

    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPending]);

  useEffect(() => {
    setShowUploadConfirmation(false);
    if (isUploadDocumentDetailsSuccess || isUpdateContractDocumentSuccess) {
      setIsUploading(false);
      onClose();
      dispatch(
        toggleToast({
          open: true,
          message: isInEditMode
            ? getLabel("documentUpdated")
            : getLabel("documentUploaded"),
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
      onDocumentUploaded(documentTypeRef.current);
    }
  }, [isUploadDocumentDetailsSuccess, isUpdateContractDocumentSuccess]);

  const handleError = (errorObj: Error) => {
    const errorMessage = getParsedErrorMessage(errorObj);
    dispatch(
      toggleToast({
        open: true,
        message: errorMessage,
        type: "error",
      }),
    );
  };

  const handleOpportunityErrors = (error: Error) => {
    if (
      isErrorCode(error, BE_ERROR_CODES.OPPORTUNITY_ID_INVALID) ||
      isErrorCode(error, BE_ERROR_CODES.OPPORTUNITY_ID_INVALID_VALIDATE_API)
    ) {
      setTooltipMessage("");
      setError(UploadDocumentFormKey.opportunityId, {
        type: "custom",
        message: getLabel("opportunityIDInvalidMessage"),
      });
      setShowTooltipErrorIcon(false);
      return true;
    } else if (
      isErrorCode(
        error,
        BE_ERROR_CODES.OPPORTUNITY_ID_ALREADY_MAPPED_TO_CLIENT,
      ) ||
      isErrorCode(
        error,
        BE_ERROR_CODES.VALIDATE_API_OPPORTUNITY_ID_ALREADY_MAPPED_TO_CLIENT,
      )
    ) {
      const parsedError = getParsedBEErrors(error);
      const clientName = parsedError?.[0].message?.split("|")[1]?.trim() || "";
      setTooltipMessage(clientName);
      setError(UploadDocumentFormKey.opportunityId, {
        type: "custom",
        message: getLabel("opportunityIDAssociatedWithAnotherClient"),
      });
      setShowTooltipErrorIcon(true);
      return true;
    } else if (
      isErrorCode(
        error,
        BE_ERROR_CODES.OPPORTUNITY_ID_ALREADY_MAPPED_TO_PROGRAM,
      ) ||
      isErrorCode(
        error,
        BE_ERROR_CODES.VALIDATE_API_OPPORTUNITY_ID_ALREADY_MAPPED_TO_PROGRAM,
      )
    ) {
      const parsedError = getParsedBEErrors(error);
      const programName = parsedError?.[0].message?.split("|")[0]?.trim() || "";
      setTooltipMessage(programName);
      setError(UploadDocumentFormKey.opportunityId, {
        type: "custom",
        message: getLabel("opportunityIDAssociatedWithProgramMessage"),
      });
      setShowTooltipErrorIcon(true);
      return true;
    }
    return false;
  };

  // Handle upload document errors
  useEffect(() => {
    setShowUploadConfirmation(false);

    if (isUploadDocumentDetailsError || isfileUploadError || isLobError) {
      const errorObj =
        uploadDocumentDetailsError || fileUploadApiError || lobError;

      const isOpportunityError = uploadDocumentDetailsError
        ? handleOpportunityErrors(uploadDocumentDetailsError)
        : false;

      if (!isOpportunityError && errorObj) {
        handleError(errorObj);
      }

      setIsUploading(false);
    }
  }, [isUploadDocumentDetailsError, isfileUploadError, isLobError]);

  // Handle update contract document errors
  useEffect(() => {
    setShowUploadConfirmation(false);
    if (isUpdateContractDocumentError) {
      const isOpportunityError = handleOpportunityErrors(
        updateContractDocumentError,
      );
      if (!isOpportunityError) {
        handleError(updateContractDocumentError);
      }
      setIsUploading(false);
    }
  }, [isUpdateContractDocumentError]);

  const handleOnClose = () => {
    if (isEmpty(dirtyFields) && isEmpty(getValues("file"))) {
      onClose();
    } else {
      setIsCancelWarningVisible(true);
    }
  };

  const checkAndValidateOpportunityId = (): boolean => {
    const { documentType, opportunityId, isOpportunityIdValidated } =
      getValues();
    if (
      documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract &&
      isNotEmpty(opportunityId) &&
      !isOpportunityIdValidated
    ) {
      if (!formState.errors?.opportunityId) {
        setError(UploadDocumentFormKey.opportunityId, {
          type: "custom",
          message: getLabel("opportunityIDMustBeValidatedMessage"),
        });
      }
      return false;
    }
    return true;
  };

  const resetOpportunityIdValidation = () => {
    setTooltipMessage("");
    setShowTooltipErrorIcon(false);
    clearErrors(UploadDocumentFormKey.opportunityId);
    setValue(UploadDocumentFormKey.isOpportunityIdValidated, false);
    setValue(UploadDocumentFormKey.opportunityName, "");
  };

  useEffect(() => {
    if (isValidateOpportunityIdSuccess && validateOpportunityIdData) {
      setValue(UploadDocumentFormKey.isOpportunityIdValidated, true);
      clearErrors(UploadDocumentFormKey.opportunityId);
      setValue(
        UploadDocumentFormKey.opportunityName,
        getValueOrBlank(validateOpportunityIdData?.name),
      );

      setShowTooltipErrorIcon(false);
    }
  }, [isValidateOpportunityIdSuccess, validateOpportunityIdData]);

  useEffect(() => {
    if (isValidateOpportunityIdError && validateOpportunityIdError) {
      handleOpportunityErrors(validateOpportunityIdError);
    }
  }, [isValidateOpportunityIdError, validateOpportunityIdError]);

  const handleOnUpload = () => {
    if (!checkAndValidateOpportunityId()) return;

    trigger().then((isValid) => {
      if (isValid) {
        handleSubmit(() => setShowUploadConfirmation(true))();
      }
    });
  };

  const getLabel = (key: keyof typeof uploadDocumentSiteCoreKeys) => {
    return fetchLabel(uploadDocumentTitle, uploadDocumentSiteCoreKeys[key]);
  };

  const modalTitle = useMemo(() => {
    if (isInEditMode)
      return replaceHashWithValue(
        getLabel("editDocumentHeading"),
        getValueOrBlank(data?.documentName),
      );

    return fetchComponentTitle(uploadDocumentTitle);
  }, []);

  useEffect(() => {
    getGetPaymentTerms();
  }, []);

  const handleExpirationDateChange = (newValue: dayjs.Dayjs | null) => {
    if (newValue === null) {
      return;
    }
    const formattedDate = newValue.format(userCountryDateFormat);
    setValue(
      UploadDocumentFormKey.sowStatusId,
      formattedDate
        ? getDocumentStatusFromExpirationDate(formattedDate)
        : STATUS_BADGE_TYPE.active,
    );
  };

  const handleContractEffectiveAndExpireDateChange = (
    sowContractEffectiveDate: dayjs.Dayjs | null,
    sowExpirationDate: dayjs.Dayjs | null,
  ) => {
    if (isEmpty(sowContractEffectiveDate) && isEmpty(sowExpirationDate)) {
      setValue(UploadDocumentFormKey.sowStatusId, STATUS_BADGE_TYPE.active);
    } else if (isNotEmpty(sowContractEffectiveDate)) {
      const status = getDocumentStatusFromEffectiveDate(
        sowContractEffectiveDate,
      );
      if (isNotEmpty(status)) {
        setValue(UploadDocumentFormKey.sowStatusId, status);
      } else {
        handleExpirationDateChange(sowExpirationDate);
      }
    } else {
      handleExpirationDateChange(sowExpirationDate);
    }
  };

  const isSingleCountry = useMemo(
    () =>
      getValues(UploadDocumentFormKey.contractGeoScopeId) ===
      "GeoScope.SingleCountry",
    [watch(UploadDocumentFormKey.contractGeoScopeId)],
  );

  useEffect(() => {
    const requiredCount = isSingleCountry ? 1 : 2;
    errorMessageRef.current =
      requiredCount === 1
        ? getLabel("errorSelectAtLeastOneCountry")
        : getLabel("errorSelectAtLeastTwoCountry");
    setMinCountriesRequired(requiredCount);
  }, [isSingleCountry]);

  const uploadDocument = async () => {
    const { documentType, file } = getValues();
    return await fileUpload({
      documentType:
        documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract
          ? (CLIENT_DOCUMENT_TYPES.clientContract as DocumentType)
          : (CLIENT_DOCUMENT_TYPES.clientOthers as DocumentType),
      entityId: isInEditMode ? (data?.id ?? clientId) : clientId,
      file: file,
      operationType: isInEditMode ? "update" : "add",
      entityType: "client",
    });
  };

  const handleUploadDocument = async () => {
    const response = await getUploadResponse();
    if (!response?.success) return;

    let params: DocumentUploadParams<"client_contract" | "client_others">;
    params =
      getValues().documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract
        ? buildContractParams(response)
        : buildOtherParams(response);

    documentTypeRef.current = params.documentType;

    isInEditMode
      ? updateContractDocument({
          ...params,
          id: getValueOrBlank(data?.id),
        })
      : uploadDocumentDetails(params);
  };

  const getUploadResponse = async () => {
    if (!isInEditMode) return uploadDocument();

    return isNewFileUploadedRef.current
      ? uploadDocument()
      : {
          documentId: data?.documentId,
          fileName: data?.documentName,
          success: true,
        };
  };

  const optionalField = (key: string, value: any) =>
    value ? { [key]: value } : {};

  const optionalNumber = (key: string, value: any) =>
    value ? { [key]: Number(value) } : {};

  const buildContractParams = (response: {
    documentId: string | undefined;
    fileName: string | undefined;
  }): DocumentUploadParams<"client_contract"> => {
    const {
      classificationId,
      contractDuration,
      contractGeoScopeCountries,
      contractGeoScopeSingleCountry,
      contractGeoScopeId,
      customDeliverablesId,
      engagementTypeId,
      exclusivityId,
      notes,
      ownershipCountryId,
      paymentTermId,
      serviceLevelAgreementsId,
      sowAutoRenewalClauseId,
      sowAutoRenewalTerms,
      sowExpirationDate,
      sowContractEffectiveDate,
      contractProductLines,
      opportunityId,
      opportunityName,
    } = getValues();

    let updatedContractGeoScopeCountries = contractGeoScopeCountries;
    if (
      isSingleCountry &&
      contractGeoScopeSingleCountry &&
      isNotEmpty(contractGeoScopeSingleCountry)
    ) {
      updatedContractGeoScopeCountries = [contractGeoScopeSingleCountry];
    }
    return {
      clientId: clientId,
      documentType: "client_contract",
      documentPayload: {
        sowStatusId: STATUS_BADGE_TYPE.active,
        uploadDate: dayjs().format(),
        documentName: response.fileName,
        documentId: response.documentId,
        ...optionalField("ownershipCountryId", ownershipCountryId),
        ...optionalField("exclusivityId", exclusivityId),
        ...optionalField("sowAutoRenewalClauseId", sowAutoRenewalClauseId),
        ...optionalNumber(
          "sowAutoRenewalTerms",
          isContractAutoRenewalClause ? sowAutoRenewalTerms : "",
        ),
        ...(sowExpirationDate && {
          sowExpirationDate: convertToUTCFormat(
            sowExpirationDate,
            userCountryDateFormat,
            true,
          ),
        }),
        ...optionalField("classificationId", classificationId),
        ...optionalField("serviceLevelAgreementsId", serviceLevelAgreementsId),
        ...optionalField("customDeliverablesId", customDeliverablesId),
        ...optionalField("paymentTermId", paymentTermId),
        ...optionalNumber("contractDuration", contractDuration),
        ...optionalField("engagementTypeId", engagementTypeId),
        ...(sowContractEffectiveDate && {
          sowContractEffectiveDate: convertToUTCFormat(
            sowContractEffectiveDate,
            userCountryDateFormat,
          ),
        }),
        ...optionalField("notes", notes),
        contractGeoScopeId: contractGeoScopeId || "",
        contractGeoScopeCountries: updatedContractGeoScopeCountries as string[],
        productLineId: contractProductLines as string[],
        ...optionalField("opportunityId", opportunityId),
        ...optionalField("opportunityName", opportunityName),
      },
    };
  };

  const buildOtherParams = (response: {
    documentId: string | undefined;
    fileName: string | undefined;
  }): DocumentUploadParams<"client_others"> => {
    const { notes, statusId, effectiveDate } = getValues();

    return {
      clientId: clientId,
      documentType: "client_others",
      documentPayload: {
        statusId: (statusId as StatusBadgeType) ?? STATUS_BADGE_TYPE.active,
        uploadDate: dayjs().format(),
        ...optionalField("notes", notes),
        ...optionalField("documentName", response.fileName),
        ...optionalField("documentId", response.documentId),
        ...(effectiveDate && {
          effectiveDate: convertToUTCFormat(
            effectiveDate,
            userCountryDateFormat,
          ),
        }),
      },
    };
  };

  const formatDateValue = (value?: string | null) => {
    return isNotEmpty(value) ? dayjs(value, userCountryDateFormat) : null;
  };

  const handleDocumentUpdate = (date: Date | null) => {
    isNewFileUploadedRef.current = true;
    setDocUploadDate(date ? dayjs(date).format(userCountryDateFormat) : "");
  };

  const clearCountriesError = () => {
    const { errors } = formState;
    if (isNotEmpty(errors)) {
      clearErrors(UploadDocumentFormKey.contractGeoScopeCountries);
    }
  };

  const handleItemChangedOrDropdownClosed = (selectedValues: Array<string>) => {
    setValue(UploadDocumentFormKey.contractGeoScopeCountries, selectedValues, {
      shouldValidate: true,
    });
    if (selectedValues?.length < 2) {
      setError(UploadDocumentFormKey.contractGeoScopeCountries, {
        type: "custom",
        message: getLabel("errorSelectAtLeastTwoCountry"),
      });
    } else {
      clearCountriesError();
    }
  };

  const handleEffectiveDateValidation = (newValue: dayjs.Dayjs | null) => {
    setValue(UploadDocumentFormKey.sowEffectiveDateWithoutFormatted, newValue);
    const sowExpirationDate = getValues(
      UploadDocumentFormKey.sowExpirationDate,
    );
    const dayJsSowExpirationDate = sowExpirationDate
      ? dayjs(sowExpirationDate, userCountryDateFormat)
      : null;
    handleContractEffectiveAndExpireDateChange(
      newValue,
      dayJsSowExpirationDate,
    );
  };
  const handleExpireDateValidation = (newValue: dayjs.Dayjs | null) => {
    setValue(UploadDocumentFormKey.sowExpiryDateWithoutFormatted, newValue);
    const sowEffectiveDate = getValues(
      UploadDocumentFormKey.sowContractEffectiveDate,
    );
    const dayJsSowEffectiveDate = sowEffectiveDate
      ? dayjs(sowEffectiveDate, userCountryDateFormat)
      : null;
    handleContractEffectiveAndExpireDateChange(dayJsSowEffectiveDate, newValue);
  };

  const isDocumentStatusExpired = () => {
    const { sowStatusId } = getValues();
    return sowStatusId === STATUS_BADGE_TYPE.expired;
  };

  const handleOpportunityIdValidation = () => {
    const { opportunityId } = getValues();
    if (isNotEmpty(opportunityId)) {
      const validateOpportunityPayload: ValidateOpportunityPayload = {
        clientId: getValueOrBlank(clientId),
        opportunityId: getValueOrBlank(opportunityId),
        contractDocumentId: "",
        programId: "",
        isForDocument: true,
        isForProgram: false,
      };
      validateOpportunityId(validateOpportunityPayload);
    }
  };

  return (
    <FormProvider {...methods}>
      <MuiModal
        open={open}
        onClose={handleOnClose}
        title={modalTitle}
        hasLargeContent
        disablePrimaryButton={disableUpload}
        primaryButtonTitle={buttonUpload}
        secondaryButtonTitle={buttonCancel}
        onSecondaryButtonClick={handleOnClose}
        onPrimaryButtonClick={handleOnUpload}
        className="upload-new-document-modal"
      >
        <UploadDocumentModalWrapper>
          <AccordionHeader
            title={
              <AccordionHeaderText label={getLabel("documentDetailsHeading")} />
            }
            isExpanded={true}
            showExpandCollapseIcon={false}
          >
            <MuiBox className="content separator">
              <MuiGrid container spacing={gridSpacing}>
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={UploadDocumentFormKey.documentType}
                  >
                    <MuiSelect
                      id={`${pageId}-${UploadDocumentFormKey.documentType}`}
                      label={getLabel("documentType")}
                      placeholder={commonLabels.selectPlaceHolder}
                      options={getDropdownList("documentTypeList")}
                      onChange={(e) => {
                        handleDocumentTypeChange(e.target.value as string);
                      }}
                      disabled={isInEditMode}
                      {...(isInEditMode && { className: "sow-status" })}
                      isMandatory
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                {isInEditMode &&
                getValues(UploadDocumentFormKey.documentType) ===
                  DOCUMENT_SITE_CORE_KEY.documentTypeContract ? (
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.contractId}
                    >
                      <MuiTextField
                        id={`${pageId}-${UploadDocumentFormKey.contractId}`}
                        label={getLabel("contractId")}
                        requiredARIALabel={commonARIALabel.required}
                        disabled
                      />
                    </FormController>
                  </MuiGrid>
                ) : null}
              </MuiGrid>
            </MuiBox>
            {getValues(UploadDocumentFormKey.documentType) ===
            DOCUMENT_SITE_CORE_KEY.documentTypeOthers ? (
              <MuiBox className="content">
                <MuiGrid
                  container
                  spacing={gridSpacing}
                  className="contact-type-container"
                >
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.statusId}
                    >
                      <MuiSelect
                        id={`${pageId}-${UploadDocumentFormKey.statusId}`}
                        label={getLabel("status")}
                        disabled={!isInEditMode}
                        placeholder={
                          isInEditMode
                            ? commonLabels.selectPlaceHolder
                            : fetchValueByKeyOfDropdown(
                                uploadDocumentTitle,
                                uploadDocumentSiteCoreKeys.othersStatusList,
                                STATUS_BADGE_TYPE.active,
                              )
                        }
                        options={filteredStatusOptions}
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.uploadDate}
                    >
                      <MuiTextField
                        id={`${pageId}-${UploadDocumentFormKey.uploadDate}`}
                        label={getLabel("uploadDate")}
                        disabled={true}
                        placeholder={docUploadDate || userCountryDateFormat}
                        className="upload-date"
                        endAdornment={
                          <InputAdornment position="end">
                            <CalendarIcon />
                          </InputAdornment>
                        }
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDateFieldFormController
                      control={control}
                      name={UploadDocumentFormKey.effectiveDate}
                      id={`${pageId}-${UploadDocumentFormKey.effectiveDate}`}
                      onChange={handleOtherDocumentEffectiveDateChange}
                      value={formatDateValue(
                        getValues(UploadDocumentFormKey.effectiveDate),
                      )}
                      label={getLabel("effectiveDate")}
                      format={userCountryDateFormat}
                      valueFormat={userCountryDateFormat}
                      className="date-picker"
                      requiredARIALabel={commonARIALabel.required}
                      selectDateARIALabel={commonARIALabel.selectDate}
                      clearable
                    />
                  </MuiGrid>
                </MuiGrid>
              </MuiBox>
            ) : (
              <MuiBox className="content">
                <MuiGrid
                  container
                  spacing={gridSpacing}
                  className="contact-type-container"
                >
                  <MuiGrid size={gridSize}>
                    <MuiBox className="opportunity-id-container">
                      <FormController
                        control={control}
                        name={UploadDocumentFormKey.opportunityId}
                      >
                        <MuiTextField
                          name={UploadDocumentFormKey.opportunityId}
                          label={getLabel("opportunityID")}
                          placeholder={commonLabels.inputPlaceHolder}
                          isErrorInfoIconEnabled={showTooltipErrorIcon}
                          tooltipMessage={tooltipMessage}
                          tooltipHeaderTitle={commonLabels.details}
                          maxLength={MAX_LENGTH_50}
                          onInput={resetOpportunityIdValidation}
                          requiredARIALabel={commonARIALabel.required}
                        />
                      </FormController>
                      <MuiLink
                        className="validate-link"
                        onClick={handleOpportunityIdValidation}
                      >
                        {commonLabels.validate}
                      </MuiLink>
                    </MuiBox>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.opportunityName}
                    >
                      <MuiTextField
                        isMandatory
                        disabled
                        label={getLabel("opportunityName")}
                        placeholder={EMPTY_VALUE_DASH}
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.ownershipCountryId}
                    >
                      <MuiSelect
                        id={`${pageId}-${UploadDocumentFormKey.ownershipCountryId}`}
                        label={getLabel("countryOfOwnership")}
                        visibleDefaultSelectOption
                        placeholder={commonLabels.selectPlaceHolder}
                        options={setApiDataToDropDownOption(
                          countryListData,
                          true,
                        )}
                        requiredARIALabel={commonARIALabel.required}
                        showSearch
                        searchPlaceholder={commonLabels.search}
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.exclusivityId}
                    >
                      <MuiSelect
                        id={`${pageId}-${UploadDocumentFormKey.exclusivityId}`}
                        label={getLabel("exclusivity")}
                        visibleDefaultSelectOption
                        placeholder={commonLabels.selectPlaceHolder}
                        options={getDropdownList("exclusivityList")}
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.sowAutoRenewalClauseId}
                    >
                      <MuiSelect
                        id={`${pageId}-${UploadDocumentFormKey.sowAutoRenewalClauseId}`}
                        label={getLabel("sowAutoRenewalClause")}
                        visibleDefaultSelectOption
                        placeholder={commonLabels.selectPlaceHolder}
                        options={getDropdownList("autoRenewalClauseList")}
                        requiredARIALabel={commonARIALabel.required}
                        disabled={isDocumentStatusExpired()}
                      />
                    </FormController>
                  </MuiGrid>
                  {isContractAutoRenewalClause !== false && (
                    <MuiGrid size={gridSize}>
                      <FormController
                        control={control}
                        name={UploadDocumentFormKey.sowAutoRenewalTerms}
                      >
                        <MuiTextField
                          id={`${pageId}-${UploadDocumentFormKey.sowAutoRenewalTerms}`}
                          label={getLabel("sowAutoRenewalTerms")}
                          placeholder={commonLabels.inputPlaceHolder}
                          type="number"
                          inputNumberWithoutDecimal
                          isMandatory={isContractAutoRenewalClause}
                          positiveValuesOnly
                          requiredARIALabel={commonARIALabel.required}
                        />
                      </FormController>
                    </MuiGrid>
                  )}
                  <MuiGrid size={gridSize}>
                    <MuiDateFieldFormController
                      control={control}
                      name={UploadDocumentFormKey.sowContractEffectiveDate}
                      id={`${pageId}-${UploadDocumentFormKey.sowContractEffectiveDate}`}
                      value={formatDateValue(
                        getValues("sowContractEffectiveDate"),
                      )}
                      label={getLabel("sowContractEffectiveDate")}
                      format={userCountryDateFormat}
                      valueFormat={userCountryDateFormat}
                      clearable
                      className="date-picker"
                      onChange={handleEffectiveDateValidation}
                      requiredARIALabel={commonARIALabel.required}
                      selectDateARIALabel={commonARIALabel.selectDate}
                      isMandatory
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDateFieldFormController
                      control={control}
                      name={UploadDocumentFormKey.sowExpirationDate}
                      id={`${pageId}-${UploadDocumentFormKey.sowExpirationDate}`}
                      value={formatDateValue(getValues("sowExpirationDate"))}
                      label={getLabel("sowExpirationDate")}
                      format={userCountryDateFormat}
                      valueFormat={userCountryDateFormat}
                      className="date-picker"
                      onChange={handleExpireDateValidation}
                      requiredARIALabel={commonARIALabel.required}
                      selectDateARIALabel={commonARIALabel.selectDate}
                      clearable
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.sowStatusId}
                    >
                      <MuiSelect
                        id={`${pageId}-${documentSiteCoreKeys.status}`}
                        className="sow-status"
                        label={getLabel("status")}
                        visibleDefaultSelectOption
                        disabled={true}
                        placeholder={commonLabels.selectPlaceHolder}
                        options={getDropdownList("contractStatusList")}
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.classificationId}
                    >
                      <MuiSelect
                        id={`${pageId}-${UploadDocumentFormKey.classificationId}`}
                        label={getLabel("classification")}
                        visibleDefaultSelectOption
                        isMandatory
                        placeholder={commonLabels.selectPlaceHolder}
                        options={getDropdownList("classificationList")}
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.serviceLevelAgreementsId}
                    >
                      <MuiSelect
                        id={`${pageId}-${UploadDocumentFormKey.serviceLevelAgreementsId}`}
                        label={getLabel("serviceLevelAgreements")}
                        visibleDefaultSelectOption
                        placeholder={commonLabels.selectPlaceHolder}
                        options={getDropdownList("serviceLevelAgreementList")}
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.customDeliverablesId}
                    >
                      <MuiSelect
                        id={`${pageId}-${UploadDocumentFormKey.customDeliverablesId}`}
                        label={getLabel("customDeliverables")}
                        visibleDefaultSelectOption
                        placeholder={commonLabels.selectPlaceHolder}
                        options={getDropdownList("customDeliverablesList")}
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.paymentTermId}
                    >
                      <MuiSelect
                        id={`${pageId}-${UploadDocumentFormKey.paymentTermId}`}
                        label={getLabel("paymentTermsDropDown")}
                        options={setApiDataToDropDownOption(
                          paymentTermsList ?? [],
                        )}
                        isMandatory
                        onChange={(e: SelectChangeEvent<unknown>) => {
                          setValue(
                            UploadDocumentFormKey.paymentTermId,
                            e.target.value as string,
                          );
                        }}
                        requiredARIALabel={commonARIALabel.required}
                        placeholder={commonLabels.selectPlaceHolder}
                        showSearch
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.contractDuration}
                    >
                      <MuiTextField
                        id={`${pageId}-${UploadDocumentFormKey.contractDuration}`}
                        label={getLabel("durationOfContractSOWTerm")}
                        placeholder={commonLabels.inputPlaceHolder}
                        type="number"
                        inputNumberWithoutDecimal
                        positiveValuesOnly
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.engagementTypeId}
                    >
                      <MuiSelect
                        id={`${pageId}-${UploadDocumentFormKey.engagementTypeId}`}
                        label={getLabel("engagementType")}
                        visibleDefaultSelectOption
                        placeholder={commonLabels.selectPlaceHolder}
                        options={getDropdownList("engagementTypeList")}
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>

                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.uploadDate}
                    >
                      <MuiTextField
                        id={`${pageId}-${UploadDocumentFormKey.uploadDate}`}
                        label={getLabel("uploadDate")}
                        disabled={true}
                        placeholder={docUploadDate || userCountryDateFormat}
                        className="upload-date"
                        endAdornment={
                          <InputAdornment position="end">
                            <CalendarIcon />
                          </InputAdornment>
                        }
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.contractProductLines}
                      ref={productLinesRef}
                    >
                      <MuiMultiSelect
                        label={getLabel("contractProductLineDropdown")}
                        options={setApiDataToDropDownOption(lineOfBusinesses)}
                        showSearch
                        showSelectAllOption
                        showChip
                        id={`${pageId}-${UploadDocumentFormKey.contractProductLines}`}
                        dialogTitle={getLabel("contractProductLineDropdown")}
                        selectPlaceHolder={commonLabels.selectPlaceHolder}
                        searchPlaceHolder={commonLabels.search}
                        selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                        doneText={commonLabels.done}
                        isMandatory
                        allARIAProps={multiSelectARIALabels}
                      />
                    </FormController>
                  </MuiGrid>
                </MuiGrid>
                <MuiGrid
                  container
                  spacing={gridSpacing}
                  className="contact-type-container geo-scope-container"
                >
                  <MuiGrid size={gridSize}>
                    <FormController
                      control={control}
                      name={UploadDocumentFormKey.contractGeoScopeId}
                    >
                      <MuiSelect
                        id={`${pageId}-${UploadDocumentFormKey.contractGeoScopeId}`}
                        label={getLabel("contractGeoScope")}
                        inputRef={contractGeoScopeIdRef}
                        isMandatory
                        visibleDefaultSelectOption
                        onChange={(e) => {
                          handleChangeGeoScope(e.target.value as string);
                        }}
                        placeholder={commonLabels.selectPlaceHolder}
                        options={fetchDropdownValues(
                          documentSiteCoreKeys.documentsTitle,
                          documentSiteCoreKeys.geoScopeList,
                        )}
                        requiredARIALabel={commonARIALabel.required}
                      />
                    </FormController>
                  </MuiGrid>

                  {getValues("contractGeoScopeId") && (
                    <MuiGrid size={gridSize}>
                      {isSingleCountry ? (
                        <FormController
                          control={control}
                          name={
                            UploadDocumentFormKey.contractGeoScopeSingleCountry
                          }
                        >
                          <MuiSelect
                            id={`${pageId}-${UploadDocumentFormKey.contractGeoScopeSingleCountry}`}
                            label={getLabel("contractGeoScopeDetail")}
                            visibleDefaultSelectOption
                            isMandatory
                            showSearch
                            placeholder={commonLabels.selectPlaceHolder}
                            options={setApiDataToDropDownOptionForCountry(
                              geoScopeCountryList,
                            )}
                            requiredARIALabel={commonARIALabel.required}
                          />
                        </FormController>
                      ) : (
                        <FormController
                          control={control}
                          name={UploadDocumentFormKey.contractGeoScopeCountries}
                        >
                          <MuiMultiSelect
                            id={`${pageId}-${UploadDocumentFormKey.contractGeoScopeCountries}`}
                            label={getLabel("contractGeoScopeDetail")}
                            options={setApiDataToDropDownOptionForCountry(
                              geoScopeCountryList,
                            )}
                            placeholder={commonLabels.selectPlaceHolder}
                            selectedValues={geoScopeCountries}
                            onItemsChangedOrDropdownClosed={(updatedValues) => {
                              const filteredStringValues = updatedValues.filter(
                                (value): value is string =>
                                  typeof value === "string",
                              );
                              handleItemChangedOrDropdownClosed(
                                filteredStringValues,
                              );
                              setGeoScopeCountries(filteredStringValues);
                            }}
                            showSelectAllOption
                            isChipEditable={true}
                            showChip={true}
                            showSearch={true}
                            reset={resetGeoScopeCountries}
                            isMandatory
                            dialogTitle={commonLabels.selectedCountries}
                            selectPlaceHolder={commonLabels.selectPlaceHolder}
                            searchPlaceHolder={commonLabels.search}
                            selectAllPlaceHolder={
                              commonLabels.selectAllPlaceHolder
                            }
                            doneText={commonLabels.done}
                            allARIAProps={multiSelectARIALabels}
                          />
                        </FormController>
                      )}
                    </MuiGrid>
                  )}
                </MuiGrid>
              </MuiBox>
            )}
            <MuiBox className="content">
              <MuiBox className="upload-document-notes">
                <FormController
                  control={control}
                  name={UploadDocumentFormKey.notes}
                >
                  <MuiTextField
                    id={`${pageId}-${UploadDocumentFormKey.notes}`}
                    label={commonLabels.notes}
                    placeholder={commonLabels.inputPlaceHolder}
                    className="notes-input"
                    multiline
                    rows={4}
                    data-testid={`${pageId}-${UploadDocumentFormKey.notes}`}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiBox>
            </MuiBox>
            <MuiBox />
          </AccordionHeader>
          <MuiBox>
            <AccordionHeader
              title={
                <AccordionHeaderText label={getLabel("attachmentHeading")} />
              }
              isExpanded={true}
              showExpandCollapseIcon={false}
            >
              <MuiBox>
                <FormController
                  control={control}
                  name={UploadDocumentFormKey.file}
                >
                  <FileUploader
                    label={getLabel("uploadDocument")}
                    isMandatory
                    onDocumentChange={(date) => handleDocumentUpdate(date)}
                    initialFile={uploadedFile}
                    onUploadError={(error) => setFileUploadError(error)}
                    formNameKey={UploadDocumentFormKey.file}
                    data-testid={`${pageId}-${UploadDocumentFormKey.file}`}
                    clearUploadedFile={clearFile}
                    maxFileSizeErrorMessageText={fetchLabel(
                      uploadDocumentTitle,
                      maxFileSizeErrorMessage,
                    )}
                    fileFormatErrorMessageText={fetchLabel(
                      uploadDocumentTitle,
                      fileFormatErrorMessage,
                    )}
                    sizeLabel={fetchLabel(
                      uploadDocumentTitle,
                      maximumFileSizeAllowed,
                    )}
                    fileTypeLabel={fetchLabel(
                      uploadDocumentTitle,
                      allowedFileTypes,
                    )}
                    browseLabel={commonLabels.browse}
                    dropYourFileLabel={commonLabels.dropYourFile}
                    browseImage={{
                      src: uploadImage?.ImageUrl,
                      alt: uploadImage?.AltText,
                    }}
                  />
                </FormController>
              </MuiBox>
            </AccordionHeader>
          </MuiBox>
          {isCancelWarningVisible && (
            <WarningConfirmationModal
              show={isCancelWarningVisible}
              onClose={() => {
                setIsCancelWarningVisible(false);
              }}
              onPrimaryButtonClick={() => {
                setIsCancelWarningVisible(false);
                onClose();
              }}
              secondaryButtonTitle={commonLabels.no}
              primaryButtonTitle={commonLabels.yes}
              labelContent={commonLabels.cancelButtonActionText}
            />
          )}
          {showUploadConfirmation && (
            <WarningConfirmationModal
              show={showUploadConfirmation}
              modalTitle={getLabel(
                isInEditMode
                  ? "updateDocumentConfirmationPopupHeading"
                  : "confirmation",
              )}
              onClose={() => {
                setShowUploadConfirmation(false);
              }}
              onPrimaryButtonClick={() => {
                handleUploadDocument();
              }}
              secondaryButtonTitle={commonLabels.no}
              primaryButtonTitle={commonLabels.yes}
              labelContent={getLabel(
                isInEditMode
                  ? "updateDocumentConfirmationPopupDescription"
                  : "actionText",
              )}
            />
          )}
        </UploadDocumentModalWrapper>
      </MuiModal>
    </FormProvider>
  );
};
